/**
 * /orders/school + /settings/my-school data layer.
 *
 * Reads + the school-forecast submit flow. The cart mutations wrap the
 * REST endpoints `POST /api/school-orders/carts` +
 * `POST /api/school-orders/carts/:id/items` +
 * `POST /api/school-orders/carts/:id/submit`. The same `CartManager`
 * primitive backs the GraphQL `submitCart` mutation.
 */

import { formatGradeRangeCodeLabel } from '@ordo/constants/grade-matcher'
import type { DayOfWeek } from '@ordo/recurrence'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type { PriceChangeWire } from '@/lib/orders/price-change'
import type { CheckoutGroup } from '@/lib/school-orders/cart'
import { groupKey, isDerivedQuantity } from '@/lib/school-orders/cart'
import { WHOLE_SCHOOL_COLUMN_ID } from '@/lib/school-orders/columns'
import { overridesFromQuantities } from '@/lib/school-orders/component-quantities'
import { classifyGroupSubmitError, type GroupSubmitFailure } from '@/lib/school-orders/price-change'

import { ApiError, apiFetch } from './http'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AdminSchoolGradeRange {
  id: string
  sortOrder: number
  /** Per-campus column label; falls back to the grade-range code label. */
  customName: string | null
  /**
   * Current-term enrolled count in this grade range, derived server-side
   * from Enrollment rows (planning population fallback). `null` when the
   * campus has no data (unknown — distinct from "0 students").
   */
  studentCount: number | null
  /**
   * EFFECTIVE USDA service style for this age-group column, resolved
   * server-side (column override → campus → org). Lets one campus mix
   * programs (straight-serve PK next to OVS K-8): component ordering is
   * gated per column, not per campus. `null` = not provisioned (treated
   * like SS).
   */
  serviceStyle: 'OVS' | 'SS' | null
  gradeRange: {
    id: string
    code: string
    name: string
    minGrade: string | null
    maxGrade: string | null
  }
}

/** The display label for an order column (custom name wins). */
export function gradeRangeColumnLabel(row: AdminSchoolGradeRange): string {
  const codeLabel = formatGradeRangeCodeLabel(row.gradeRange.code)
  const customName = row.customName?.trim()
  if (customName && customName.toLowerCase() !== row.gradeRange.name.trim().toLowerCase()) {
    return customName
  }
  return codeLabel ?? row.gradeRange.name
}

/** The five assignable enrollment dimensions a non-AGE_GROUP campus can order by. */
export type OrderGroupDimension = 'GRADE' | 'TEACHER' | 'ROOM' | 'HOMEROOM' | 'LUNCH_PERIOD'

/**
 * A non-AGE_GROUP order column. Columns are vocabulary-driven: the
 * `(groupDimension, groupValue)` tuple is the column identity and `name`
 * is the vocabulary value. The portion tier (`gradeRange`) resolves
 * server-side via the canonical fallback chain; null when underivable.
 */
export interface AdminSchoolGroupColumn {
  /** `"<groupDimension>::<groupValue>"` — the stable column id. */
  id: string
  name: string
  gradeRange: { id: string; code: string; name: string } | null
  /** Live enrolled count ?? vocabulary planning population ?? null (unknown). */
  studentCount: number | null
  sortOrder: number
  groupDimension: OrderGroupDimension
  groupValue: string
}

/** One mealtime's order-band partition on the wire (band codes are plain strings). */
export type WireOrderBandPartition = { kind: 'SINGLE' } | { kind: 'BANDS'; bands: string[] }

export interface AdminSchool {
  id: string
  name: string
  timeZone: string
  /**
   * The campus's effective ORDER grouping dimension. AGE_GROUP campuses
   * order per age-group column (`gradeRanges`); NONE campuses order as
   * one whole school (single synthetic column, bucket-less lines —
   * `wholeSchool` carries the column metadata); every other dimension
   * orders per vocabulary group column (`orderGroups`).
   */
  orderGroupBy: 'AGE_GROUP' | 'NONE' | OrderGroupDimension
  /**
   * Order-deadline policy. Fed to `@ordo/recurrence` `computeOrderDeadline` to
   * derive the cutoff. `RELATIVE_DAYS` uses `orderDeadlineRelativeDays`
   * ("N days before service"); `FIXED_WEEKLY` uses day-of-week + weeks-before.
   * `orderDeadlineDayOfWeek` is null when no weekly policy is provisioned.
   */
  orderDeadlineMode: 'FIXED_WEEKLY' | 'RELATIVE_DAYS'
  orderDeadlineDayOfWeek: DayOfWeek | null
  orderDeadlineWeeksBefore: number
  orderDeadlineTime: string
  orderDeadlineRelativeDays: number | null
  /**
   * Effective USDA service style at CAMPUS level — campus override ?? org
   * default. OVS unlocks component ordering: the order-percentages settings
   * card and order-sheet prefill. `null` means not provisioned (treated
   * like SS). Age-group columns may resolve differently per column via
   * `gradeRanges[].serviceStyle` (already resolved server-side).
   */
  serviceStyle: 'OVS' | 'SS' | null
  /**
   * v2 Pre-fill settings — apply the component split as the bundle default.
   *
   * DERIVED server-side from the cascading `componentSplitMode` (campus, else
   * district, else "no opt-out"), not read off the retired boolean column.
   * Kept under its original name so the order-sheet consumers are unchanged.
   */
  prefillComponentSplitEnabled: boolean
  /**
   * The campus's own split opt-out (`null` = inheriting the district) and the
   * district's. Together with `prefillComponentSplitEnabled` these let a
   * surface say WHERE an off state came from — "off at this campus" vs "off
   * for the district" — instead of rendering a section header with nothing
   * under it, which is what produced the incident this cascade fixes.
   */
  componentSplitMode: 'ENABLED' | 'DISABLED' | null
  organizationComponentSplitMode: 'ENABLED' | 'DISABLED' | null
  /** v2 Pre-fill settings — seed order quantities from 8-week history. */
  prefillMealSelectionEnabled: boolean
  /**
   * Customers-administered display toggle: render each item's per-unit
   * price inline on the order sheet, next to the kind label ("Meal •
   * $6.99"). Off by default — most school programs deliberately keep
   * prices off the planning grid.
   */
  showItemPricesOnOrderTable: boolean
  /**
   * Mealtimes this campus serves (LocationMealtimeService rows, else the
   * org default), chronological. Drives the per-mealtime Pre-fill split
   * cards; empty = not provisioned yet.
   */
  servedMealtimes: Mealtime[]
  /**
   * Sales-tax inputs for this school's bulk orders, from the submit
   * pipeline's own config reads (server `resolveSchoolOrderTaxInfo`).
   * `effectiveTaxRateBps` is the rate a SCHOOL_FORECAST submit applies —
   * 0 today (school orders are never taxed at order submit; school tax
   * is settled at invoicing). `campusTaxRateBps` (`Location.taxRateBps`)
   * and `orgTaxExempt` (`Organization.taxExempt`) are the underlying
   * campus rate + org exemption that a future school-side tax would key
   * off; FAMILY_DIRECT orders tax at the campus rate already.
   */
  tax: {
    campusTaxRateBps: number | null
    orgTaxExempt: boolean
    effectiveTaxRateBps: number
  }
  organizationId: string
  organization: {
    id: string
    name: string
    ownedAccounts: Array<{ id: string; kind: 'HOUSEHOLD' | 'ORGANIZATION'; isArchived: boolean }>
  }
  ownedAccounts: Array<{ id: string; kind: 'HOUSEHOLD' | 'ORGANIZATION'; isArchived: boolean }>
  gradeRanges: AdminSchoolGradeRange[]
  /**
   * Locked ordering portion bands (PK / K-8 / 9-12 / K-12) — the canonical
   * GradeRange rows menu-derived AGE_GROUP order columns stamp as their
   * portion tier (`@ordo/constants/portion-groups`). Optional: older
   * cached payloads predate the field.
   */
  portionBands?: Array<{ id: string; code: string; name: string }>
  /**
   * Server-resolved Grade Mapping partition per mealtime setting (Menus ->
   * Maintenance config over shipped defaults). Optional: older cached
   * payloads fall back to the client-side constant rule, and so does a
   * payload missing one mealtime's key (snack / supper predate the split).
   */
  orderBandPartition?: {
    breakfastStyle?: WireOrderBandPartition
    lunchStyle?: WireOrderBandPartition
    snackStyle?: WireOrderBandPartition
    supperStyle?: WireOrderBandPartition
  }
  /**
   * True when the org opts into a federal pattern program (NSLP / SBP /
   * CACFP / SFSP / SSO) — binds the band partition. False = PRIVATE:
   * one band-less order column per mealtime. Optional for older payloads
   * (absent is treated as pattern-bound — the conservative partition).
   */
  usdaPatternBound?: boolean
  /** Group order columns — populated only for non-AGE_GROUP campuses. */
  orderGroups: AdminSchoolGroupColumn[]
  /**
   * The campus's GRADE vocabulary values in vocabulary order — the field-trip
   * dialog's grade picker for parent-order elections. Optional: cached
   * pre-rollout payloads predate the field.
   */
  gradeOptions?: string[]
  /**
   * Whole-school ordering metadata — the single synthetic column's
   * planning population and optional portion tier. Non-null only for
   * NONE campuses.
   */
  wholeSchool?: { studentCount: number | null; gradeRangeId: string | null } | null
  /**
   * Published, item-bearing bagged (field-trip) menus at this campus —
   * one entry per live menu window. Empty when the campus has none.
   * Gates the "Field trip?" affordance and drives the field-trip
   * dialog's mealtime checkboxes + date-picker windows.
   */
  baggedMealtimes: Array<{
    mealtime: Mealtime
    startDate: string
    endDate: string
    menuId: string
    /**
     * Derived-milk context for the bagged menu (PROD-1244): its milk
     * selection + system milk carriers, null when the menu includes no
     * milk. Optional so cached pre-rollout payloads keep parsing.
     */
    milk?: BaggedMenuMilk | null
  }>
  /**
   * Published, item-bearing Fresh Backup Meals (BACK_UP line) menus at this
   * campus — one entry per live menu window with its orderable items. Empty
   * when the campus has no backup line. Availability does not control folder visibility.
   * Optional: cached pre-rollout payloads predate the field.
   */
  backUpMealtimes?: BackUpMealtimeWindow[]
  /** Explicit Customers toggles for the four Special Service folders. */
  fieldTripMealsEnabled?: boolean
  specialDietsEnabled?: boolean
  backUpMealsEnabled?: boolean
  bulkSuppliesEnabled?: boolean
  /**
   * True when the Supplies program (SUPPLIES / SWAG / SHELF_STABLE_MEALS
   * catalog) is orderable at this campus. Always false until that work
   * lands; the dialog hides its Supplies tab on false.
   */
  hasSupplyCatalog?: boolean
  /**
   * True when this campus has at least one published, item-bearing menu — i.e.
   * there is actually something to order here.
   *
   * `liveAt` marks a campus PROVISIONED, which can be months before anyone
   * authors its menus, so a "lights up later" site would otherwise sit in the
   * school-order campus selector offering an empty order sheet. The selector
   * (and the checkout campus tabs) filter on this; campus SETUP surfaces —
   * grade ranges, roster import, devices, invitations, auto-orders, district
   * split — deliberately do not, because they are what you use to stand a
   * campus up before its menus exist.
   *
   * Optional: cached pre-rollout payloads predate the field, and `undefined`
   * must read as "show it" so a stale cache can never blank the selector.
   */
  hasOrderableMenu?: boolean
  /**
   * Does this campus accept SCHOOL-placed orders? False for a PARENT-only
   * campus (its Order user omits SCHOOL). Such a campus is still listed — it
   * is the admin's school — but every ordering affordance renders read-only,
   * because submit refuses it with 409 `school_ordering_not_enabled`.
   * Computed server-side by the submit gate's own predicate
   * (`schoolOrderingAllowed`); read it through `acceptsSchoolOrders`.
   *
   * Optional: cached pre-rollout payloads predate the field. Those payloads
   * never contained a PARENT-only campus, so `undefined` reads as true.
   */
  schoolOrderingEnabled?: boolean
  /**
   * Does this campus take family-shaped orders — a parent's checkout, or the
   * school ordering for one named student? Computed server-side by the PARENT
   * gate's own predicate (`parentOrderingAllowed`). Optional: a cached payload
   * from before the field shipped reads as true (every campus but SCHOOL-only).
   */
  parentOrderingEnabled?: boolean
}

/** One live, orderable item on a published Fresh Backup Meals menu. */
export interface BackUpMenuItemSummary {
  menuItemId: string
  name: string
  sortOrder: number
  quantityCap: number | null
}

/** One published Fresh Backup Meals menu window at a campus. */
export interface BackUpMealtimeWindow {
  mealtime: Mealtime
  startDate: string
  endDate: string
  menuId: string
  items: BackUpMenuItemSummary[]
}

/**
 * A bagged menu's derived-milk context — what the day grid needs to derive
 * the standalone milk lines for placed field-trip meals exactly as the
 * server-side placer does (`FieldTripPlacementManager.resolveMilkPlan`).
 */
export interface BaggedMenuMilk {
  /** `Menu.settings.milkSelectionSource`; null on legacy rows. */
  selectionSource: 'MENU' | 'SCHOOL_PROFILE' | 'TEMPLATE' | null
  /** The menu's declared lineup (canonical codes). */
  milkOptionCodes: string[]
  /** The menu's live system milk rows — the ship/pay carriers. */
  carriers: Array<{ menuItemId: string; componentId: string | null; milkOptionCode: string | null; name: string }>
}

export interface MySchoolAdminLocationsResponse {
  me: {
    id: string
    schoolAdminLocations: AdminSchool[]
    /**
     * True when the actor IS a school admin but their organization has no
     * visible SCHOOL_CAMPUS yet (new-school onboarding gap, or campuses
     * still in draft). Drives the "campus not set up yet" empty state.
     */
    schoolAdminWithoutCampus?: boolean
  } | null
}

import type { Mealtime } from '@/lib/mealtime'
export type { Mealtime }
export type OrderBatchStatus = 'DRAFT' | 'SUBMITTED' | 'CONFIRMED' | 'FULFILLED' | 'VOIDED'

/**
 * OrderItem.status enum (mirrors the central Prisma enum). Drives the
 * status chip mapping on cells + rows.
 *
 * NOTE — the central enum today is the historical
 * UNASSIGNED/ASSIGNED/SERVED/BILLED/VOIDED set. Plan 11's spec
 * (placed/produced/served/refunded/canceled) introduces a richer
 * lifecycle that requires a schema migration; the chip helper in
 * `client/src/components/orders/school/status-chip.ts` maps both
 * vocabularies so the UI stays consistent through the transition.
 */
export type OrderItemStatus = 'UNASSIGNED' | 'ASSIGNED' | 'SERVED' | 'BILLED' | 'VOIDED'

export interface SchoolForecastBatchRow {
  id: string
  serviceDate: string
  mealtime: Mealtime
  status: OrderBatchStatus
  totalCents: number
  currency: string
  submittedAt: string | null
  /**
   * Original placer (stable across re-edits — replace never rewrites it).
   * Optional so rows cached before this enrichment stay renderable.
   */
  placedBy?: { id: string; name: string }
  items: Array<{
    id: string
    quantity: number
    menuItemId: string
    /**
     * The line's menu item is a system milk row (`lineupMilkOptionCode`) —
     * milk that derives 1:1 from the meal counts and ships as its own
     * order line. Display meal counters (day pills, calendar, batch rows)
     * exclude these; money fields keep every line. Optional server
     * enrichment — absent on older cached payloads, which then count as
     * meals (the pre-enrichment behavior).
     */
    systemMilk?: boolean
    /**
     * PROD-1515 — the item this line maps to on the menu that now covers its
     * service date, when a dated template release replaced the line's menu
     * (new item ids) after it was placed. The day grid keys the line by this
     * id so it lands on its own row. Null/absent = the line's own menu still
     * covers the date (or no unique successor item exists).
     */
    currentMenuItemId?: string | null
    /**
     * The line is a genuine field-trip line (the item's field-trip flag, or
     * an item on a bagged menu). Only these get the grid's "Field trip"
     * treatment — being off the day grid alone proves nothing. Absent on
     * older cached payloads.
     */
    fieldTrip?: boolean
    /**
     * Display name for the line's menu item. Optional server enrichment —
     * the day grid needs it only for lines whose menu is hidden from the
     * school browse projection (field-trip bagged lines); absent on older
     * payloads, where the grid falls back to a generic label.
     */
    itemName?: string | null
    /**
     * True when a chef pre-approved component swap re-rendered `itemName` —
     * the kitchen is serving a replacement. The day grid must prefer
     * `itemName` over the live-menu name for these lines.
     */
    itemNameSubstituted?: boolean
    gradeRangeId: string | null
    /**
     * The line's `(groupDimension, groupValue)` tuple — the column
     * attribution for non-AGE_GROUP lines.
     */
    groupDimension: OrderGroupDimension | 'AGE_GROUP' | null
    groupValue: string | null
    status: OrderItemStatus
    /**
     * Addendum G — per-ComponentKind breakdown from the line's child
     * OrderItems; null on legacy lines. Seeds the edit buffer so a custom
     * breakdown round-trips through re-edits.
     */
    componentBreakdown?: Record<string, number> | null
    /**
     * PROVENANCE for `componentBreakdown` — the ComponentKinds a human
     * TYPED. `null`/absent = legacy line (treated as "all typed", so no
     * quantity re-derives without an explicit reset); `[]` = every number
     * was rate-derived and free to re-derive; `["FRUIT"]` = fruit is
     * frozen, its siblings re-derive from the campus's current rates.
     */
    componentTypedKinds?: string[] | null
    /**
     * Line-level provenance. `DERIVED` marks the menu's system milk rows,
     * whose count tracks the meal counts through the campus's BEVERAGE
     * rate — so a pre-deadline edit re-derives them. Absent/`TYPED` = a
     * human's number, never moved.
     */
    quantitySource?: 'TYPED' | 'DERIVED' | null
  }>
}

/**
 * One aggregated parent-demand cell riding along on /forecast-batches —
 * committed FAMILY_DIRECT quantities keyed like batch item rows (canonical
 * portion tier for age-group campuses, `(groupDimension, groupValue)`
 * tuple for vocabulary-axis campuses; both null = unattributable, or a
 * whole-school campus where every line collapses onto the one column).
 * Backs the "+X parent orders" legend on the placed-day summary.
 */
export interface FamilyOrderCellRow {
  serviceDate: string
  mealtime: Mealtime
  menuItemId: string
  /**
   * The successor item when the parent's line was placed on a menu a dated
   * template release has since replaced (PROD-1515); null otherwise. Key the
   * cell by `currentMenuItemId ?? menuItemId` — the day grid lists the
   * successor's items. Optional on older cached payloads.
   */
  currentMenuItemId?: string | null
  gradeRangeId: string | null
  groupDimension: OrderGroupDimension | 'AGE_GROUP' | null
  groupValue: string | null
  quantity: number
  /**
   * The cell's menu item is a system milk row — combined MEAL counters
   * (cafeteria lookahead pills) exclude these, mirroring the school
   * batch items' `systemMilk` flag. Optional on older cached payloads.
   */
  systemMilk?: boolean
  /**
   * Set only when the cell has NO column attribution: 'STAFF' when the
   * diner is campus staff (teacher / school admin / cafeteria worker
   * self-orders), 'OTHER' otherwise. Lets the print sheet label its catch
   * columns by who the meals are for. Optional on older cached payloads
   * (absent reads as 'OTHER').
   */
  unattributedDinerKind?: 'STAFF' | 'OTHER' | null
  /**
   * Display name (canonical title chain, re-rendered through chef swaps —
   * PROD-225). Lets the unified print's Orders sheet name parent-only rows
   * without a per-day menus fetch. Optional on older cached payloads.
   */
  itemName?: string | null
}

// ---------------------------------------------------------------------------
// Forecast overlay (auto-order pip + day-sheet Auto-orders tab) — Plan 11
// ---------------------------------------------------------------------------

export interface SubscriptionDayFiring {
  id: string
  serviceDate: string
  label: string
  gradeRangeId: string | null
  mealtime: string | null
  studentCount: number | null
  isPaused: boolean
}

export interface ForecastOverlayDay {
  date: string
  autoOrderSubscriptionIds: string[]
  firings: SubscriptionDayFiring[]
  isClosed: boolean
}

export interface ForecastOverlayResponse {
  rangeStart: string
  rangeEndExclusive: string
  days: ForecastOverlayDay[]
}

export interface PauseSubscriptionForDateVariables {
  subscriptionId: string
  serviceDate: string
  reason?: string | null
}

export interface ResumeSubscriptionForDateVariables {
  subscriptionId: string
  serviceDate: string
}

export interface SchoolForecastBatchesForMonthResponse {
  schoolForecastBatchesForMonth: SchoolForecastBatchRow[]
  /** Parent-demand overlay for the month; [] on older cached payloads. */
  familyOrders: FamilyOrderCellRow[]
}

// ---------------------------------------------------------------------------
// PROD-1123 v2 — service history wire types
// (`GET /api/school-orders/service/{summary,day}`; shapes mirror
// `apps/ordo/server/src/managers/school-service-summary.ts`).
// ---------------------------------------------------------------------------

/**
 * Which source a mealtime's served counts came from: the signed production
 * record (authoritative), an unsigned draft tally, or raw order rows
 * (coarse — the UI says "unconfirmed").
 */
export type ServiceCountBasis = 'SIGNED_RECORD' | 'DRAFT_TALLY' | 'ROWS_ONLY'

/** One day's History-mode badge state for the week cards. */
export interface ServiceDayStatusWire {
  /** `YYYY-MM-DD` service-date key. */
  serviceDate: string
  serviceComplete: boolean
}

export interface ServiceMonthStatusResponse {
  days: ServiceDayStatusWire[]
  /**
   * `YYYY-MM-DD` days the school marked NO_SERVICE (PROD-1228) — from the same
   * gate the submit path rejects on, so a day offered here can always be
   * ordered. Optional: a cached pre-flag payload has no field, which must read
   * as "no closures known", never as "everything closed".
   */
  noServiceDates?: string[]
}

export type ServiceStudentSource = 'PARENT_ORDER' | 'POOL_DRAW' | 'POOL_SCAN' | 'REGISTER_SALE'

/** One component pick on a student's meal (serve-confirm observation). */
export interface ServiceStudentComponentWire {
  label: string
  /** Units offered to the student (0 for an off-slot extra like a milk carton). */
  ordered: number
  /** Units the student actually took. */
  served: number
}

export interface ServiceStudentServeWire {
  /** The underlying order line — the row identity (one student can carry several lines of one item). */
  orderItemId: string
  mealtime: Mealtime
  menuItemId: string
  /** Server-resolved display name (milk SKUs included) — the fallback when the item is on no grouped table. */
  itemName: string | null
  /** Milk lineup line — excluded from the meals-only anonymous-remainder math. */
  systemMilk: boolean
  gradeRangeId: string | null
  groupDimension: string | null
  groupValue: string | null
  studentUserId: string
  studentName: string | null
  /** Units ordered on this line. */
  quantity: number
  /** Units actually served (0 = ordered but never picked up). */
  served: number
  source: ServiceStudentSource
  servedAt: string | null
  familyChargedCents: number | null
  schoolCreditCents: number | null
  /** Recorded picks; empty when no serve-confirm observation exists. */
  components: ServiceStudentComponentWire[]
}

export interface ServiceComponentRowWire {
  deliverableId: string
  label: string
  ordered: number | null
  served: number | null
}

export interface ServiceMilkRowWire {
  /** Milk SKU Component id. */
  componentId: string
  label: string
  schoolOrdered: number
  parentOrdered: number
  served: number
  creditedBackCents: number
  /** À-la-carte register cartons (POS-origin SERVED/BILLED units). */
  registerSaleCount: number
  /** Family money for those cartons ($0 for lineup riders). */
  registerSaleCents: number
}

export interface ServiceMealRowWire {
  menuItemId: string
  itemName: string | null
  itemNameSubstituted: boolean
  schoolOrdered: number
  parentOrdered: number
  served: number
  voided: number
  unserved: number
  creditedBackCents: number
  /** Register events on school pre-orders (draws + seconds) — gates the "From school order" cell. */
  creditedBackCount: number
  /** À-la-carte register units (POS-origin SERVED/BILLED) — gates the "Register sales" cell. */
  registerSaleCount: number
  /** Family money charged for those register units. */
  registerSaleCents: number
  /** Student-assigned served units — drives the per-student disclosure. */
  studentServeCount: number
  components: ServiceComponentRowWire[]
}

export interface ServiceTierTotalsWire {
  schoolOrdered: number
  parentOrdered: number
  served: number
  creditedBackCents: number
  /** Register events on school pre-orders (draws + seconds) across the tier's meal rows. */
  creditedBackCount: number
  /** À-la-carte register units across meal AND milk rows. */
  registerSaleCount: number
  registerSaleCents: number
  unserved: number
  voided: number
}

export interface ServiceTierTableWire {
  gradeRangeId: string | null
  groupDimension: string | null
  groupValue: string | null
  /** gradeRange.name → groupValue → 'All students'. */
  label: string
  meals: ServiceMealRowWire[]
  milk: ServiceMilkRowWire[]
  totals: ServiceTierTotalsWire
}

export interface ServiceMealtimeDetailWire {
  mealtime: Mealtime
  basis: ServiceCountBasis
  unconfirmed: boolean
  productionRecords: Array<{ id: string; status: string; signed: boolean }>
  tiers: ServiceTierTableWire[]
}

export interface ServiceDetailsResponse {
  serviceDate: string
  currency: string
  mealtimes: ServiceMealtimeDetailWire[]
  /** Present only when fetched with includeStudents. */
  students?: ServiceStudentServeWire[]
}

export interface CreateCartVariables {
  accountId: string
  serviceLocationId?: string | null
  type?: 'STAGING' | 'SCHEDULED' | 'FAVORITE'
}

export interface AddCartItemVariables {
  cartId: string
  menuItemId: string
  date: string
  quantity: number
  /**
   * Portion TIER (`GradeRange.id`) for a column-less per-student line
   * (family flow). Column-keyed lines omit it — the server resolves the
   * tier from the AGE_GROUP column, or stamps it at submit.
   */
  gradeRangeId?: string | null
  /**
   * The `(groupDimension, groupValue)` tuple IS the column identity.
   * AGE_GROUP lines carry the age-group order column id
   * (`LocationGradeRange.id`) as the value — the server resolves the
   * portion tier (`gradeRangeId`) from it, so two columns sharing a tier
   * stay distinct lines. Every other dimension carries the vocabulary
   * value label.
   */
  groupDimension?: OrderGroupDimension | 'AGE_GROUP' | null
  groupValue?: string | null
  targetUserId?: string | null
  selectionFingerprint: string
  selections: unknown
  /**
   * Addendum G — per-ComponentKind quantities for an OVS bundle line
   * (the Meal-parts breakdown). Omit
   * to let the server default from the school's order percentages;
   * ships-complete bundles ignore it.
   */
  componentQuantityOverrides?: Record<string, number>
  /**
   * Nested milk shares — per-SKU breakdown of the BEVERAGE entry
   * (Component id → quantity, summing to the BEVERAGE override).
   * Additive: omit to let the server apportion from the school's
   * LocationMilkShareRate rows; the server also tolerates its absence.
   */
  componentSkuQuantityOverrides?: Record<string, number>
  /**
   * PROVENANCE for `componentQuantityOverrides` — the ComponentKinds a
   * human TYPED. Omit for a line with no component opinion; the server
   * then stores NULL and treats every kind as typed, so a later edit
   * re-derives nothing. `[]` means every number was rate-derived.
   */
  componentQuantitiesTypedKinds?: string[]
  /**
   * Line-level provenance. `DERIVED` marks a system-milk line whose count
   * tracks the meal counts through the campus's BEVERAGE rate, so a
   * pre-deadline edit may recompute it. Omit for a human's number.
   */
  quantitySource?: 'TYPED' | 'DERIVED'
}

export interface SubmitCartVariables {
  cartId: string
  organizationId: string
  serviceLocationId: string
  serviceDate: string
  mealtime: string
  originator?: string
  /**
   * Reprice-at-submit consent. The server 409s `price_changed` when a
   * line's price drifted from its add-time snapshot; resubmitting with
   * `true` (after the admin confirms in the PriceChangedDialog)
   * accepts the fresh prices.
   */
  acceptRepricing?: boolean
}

export interface CreateCartResponse {
  id: string
}

export interface AddCartItemResponse {
  id: string
  quantity: number
  /** Portion tier resolved server-side (AGE_GROUP lines). */
  gradeRangeId: string | null
  groupDimension: OrderGroupDimension | 'AGE_GROUP' | null
  groupValue: string | null
}

export interface SubmitCartResponse {
  id: string
  status: OrderBatchStatus
  totalCents: number
  serviceDate: string
  mealtime: Mealtime
}

// ---------------------------------------------------------------------------
// Per-student meal orders (`GET /api/school-orders/student-orders`) — the
// school print dialog's Orders CSV. Row grain, columns and order are the
// Customers "Per-student meal orders" report's; the CSV header is its
// snake_case (`lib/school-print/csv.ts`).
// ---------------------------------------------------------------------------

export interface StudentOrderRowWire {
  orderItemId: string
  /** ASCII-normalized, never shortened ("Bagel - Apple - Milk"). */
  mealName: string
  studentName: string
  /** The school's own roster id; '' when the roster never supplied one. */
  sisStudentId: string
  /** '' when no enrollment at the campus carries a grade. */
  grade: string
  quantity: number
  /** ISO `YYYY-MM-DD`. */
  serviceDate: string
  /** Raw enum text (`BREAKFAST`, `LUNCH`, …). */
  mealtime: string
  campus: string
}

export interface StudentOrdersResponse {
  rows: StudentOrderRowWire[]
}

// ---------------------------------------------------------------------------
// Fetch helpers
// ---------------------------------------------------------------------------

export async function fetchMySchoolAdminLocations(
  signal?: AbortSignal,
  profile?: 'CAFETERIA_WORKER',
  locationId?: string | null,
): Promise<MySchoolAdminLocationsResponse> {
  return apiFetch<MySchoolAdminLocationsResponse>('/api/school-orders/my-schools', {
    query: profile ? { profile, ...(locationId ? { locationId } : {}) } : undefined,
    signal,
  })
}

export async function fetchSchoolForecastBatchesForMonth(
  vars: { schoolLocationId: string; year: number; month: number },
  signal?: AbortSignal,
): Promise<SchoolForecastBatchesForMonthResponse> {
  const { batches, familyOrders } = await apiFetch<{
    batches: SchoolForecastBatchRow[]
    familyOrders?: FamilyOrderCellRow[]
  }>('/api/school-orders/forecast-batches', {
    query: { schoolLocationId: vars.schoolLocationId, year: vars.year, month: vars.month },
    signal,
  })
  return { schoolForecastBatchesForMonth: batches, familyOrders: familyOrders ?? [] }
}

export async function fetchServiceStatusForMonth(
  vars: { schoolLocationId: string; year: number; month: number },
  signal?: AbortSignal,
): Promise<ServiceMonthStatusResponse> {
  return apiFetch<ServiceMonthStatusResponse>('/api/school-orders/service/summary', {
    query: { schoolLocationId: vars.schoolLocationId, year: vars.year, month: vars.month },
    signal,
  })
}

export async function fetchServiceDetails(
  vars: { schoolLocationId: string; date: string; includeStudents?: boolean },
  signal?: AbortSignal,
): Promise<ServiceDetailsResponse> {
  return apiFetch<ServiceDetailsResponse>('/api/school-orders/service/day', {
    query: {
      schoolLocationId: vars.schoolLocationId,
      date: vars.date,
      ...(vars.includeStudents ? { includeStudents: 1 } : {}),
    },
    signal,
  })
}

export async function fetchStudentOrders(
  vars: { schoolLocationId: string; from: string; to: string },
  signal?: AbortSignal,
): Promise<StudentOrdersResponse> {
  return apiFetch<StudentOrdersResponse>('/api/school-orders/student-orders', {
    query: { schoolLocationId: vars.schoolLocationId, from: vars.from, to: vars.to },
    signal,
  })
}

export async function createCart(vars: CreateCartVariables): Promise<CreateCartResponse> {
  return apiFetch<CreateCartResponse>('/api/school-orders/carts', {
    method: 'POST',
    body: vars,
  })
}

export async function addCartItem(vars: AddCartItemVariables): Promise<AddCartItemResponse> {
  const { cartId, ...body } = vars
  return apiFetch<AddCartItemResponse>(`/api/school-orders/carts/${encodeURIComponent(cartId)}/items`, {
    method: 'POST',
    body,
  })
}

export async function submitCart(vars: SubmitCartVariables): Promise<SubmitCartResponse> {
  const { cartId, ...body } = vars
  return apiFetch<SubmitCartResponse>(`/api/school-orders/carts/${encodeURIComponent(cartId)}/submit`, {
    method: 'POST',
    body,
  })
}

/** Cancel a placed forecast batch — the "removed every item" path of an edit. */
export async function cancelSchoolBatch(batchId: string): Promise<SubmitCartResponse> {
  return apiFetch<SubmitCartResponse>(`/api/school-orders/forecast-batches/${encodeURIComponent(batchId)}/cancel`, {
    method: 'POST',
  })
}

export async function fetchForecastOverlay(
  vars: { schoolLocationId: string; year: number; month: number },
  signal?: AbortSignal,
): Promise<ForecastOverlayResponse> {
  return apiFetch<ForecastOverlayResponse>('/api/school-orders/forecast-overlays', {
    query: { schoolLocationId: vars.schoolLocationId, year: vars.year, month: vars.month },
    signal,
  })
}

export interface TypicalQuantityCell {
  menuItemId: string
  mealtime: Mealtime
  /** 0-6 per `Date.getUTCDay()` of the service date. */
  weekday: number
  /**
   * Order column id — `LocationGradeRange.id` (AGE_GROUP) or the
   * `"<dimension>::<value>"` tuple key (matches `OrderColumn.columnId`).
   */
  columnId: string
  quantity: number
  /** How many service days contributed — a confidence hint. */
  sampleCount: number
}

export interface TypicalQuantitiesResponse {
  cells: TypicalQuantityCell[]
  sampleBatches: number
}

/**
 * "Pre-fill meal selection" source: the school's typical (modal) ordered
 * quantity per (item × mealtime × weekday × order column) over the last
 * 8 weeks. Cells with no history are absent — inputs stay blank.
 */
export async function fetchTypicalQuantities(
  vars: { schoolLocationId: string },
  signal?: AbortSignal,
): Promise<TypicalQuantitiesResponse> {
  return apiFetch<TypicalQuantitiesResponse>('/api/school-orders/typical-quantities', {
    query: { schoolLocationId: vars.schoolLocationId },
    signal,
  })
}

export function useTypicalQuantitiesQuery(schoolLocationId: string | null) {
  return useQuery<TypicalQuantitiesResponse>({
    queryKey: ['school-orders', 'typical-quantities', schoolLocationId],
    queryFn: ({ signal }) => fetchTypicalQuantities({ schoolLocationId: schoolLocationId! }, signal),
    enabled: !!schoolLocationId,
    staleTime: 60_000,
  })
}

export async function pauseSubscriptionForDate(vars: PauseSubscriptionForDateVariables): Promise<void> {
  await apiFetch<void>(`/api/school-orders/subscriptions/${encodeURIComponent(vars.subscriptionId)}/pause-date`, {
    method: 'POST',
    body: { serviceDate: vars.serviceDate, reason: vars.reason ?? null },
  })
}

export async function resumeSubscriptionForDate(vars: ResumeSubscriptionForDateVariables): Promise<void> {
  await apiFetch<void>(`/api/school-orders/subscriptions/${encodeURIComponent(vars.subscriptionId)}/pause-date`, {
    method: 'DELETE',
    query: { serviceDate: vars.serviceDate },
  })
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useMySchoolAdminLocationsQuery(
  /** Resolve campuses with the CAFETERIA surface's access rules (cafeteria "Orders lookup"). */
  profile?: 'CAFETERIA_WORKER',
  /** Narrow to one campus (the cafeteria location lock) — cafeteria profile only. */
  locationId?: string | null,
  opts?: { enabled?: boolean },
) {
  return useQuery<MySchoolAdminLocationsResponse>({
    queryKey: ['school-orders', 'my-schools', profile ?? 'SCHOOL_ADMIN', locationId ?? 'all'],
    queryFn: ({ signal }) => fetchMySchoolAdminLocations(signal, profile, locationId),
    enabled: opts?.enabled ?? true,
  })
}

export function useSchoolForecastBatchesForMonthQuery(vars: { schoolLocationId: string; year: number; month: number }) {
  return useQuery<SchoolForecastBatchesForMonthResponse>({
    queryKey: ['school-orders', 'forecast-batches', vars],
    queryFn: ({ signal }) => fetchSchoolForecastBatchesForMonth(vars, signal),
    enabled: Boolean(vars.schoolLocationId),
  })
}

/** Service history is stable past data — 60s staleness is plenty. */
const SERVICE_SUMMARY_STALE_TIME_MS = 60_000

export function useSchoolServiceStatusForMonthQuery(vars: { schoolLocationId: string; year: number; month: number }) {
  return useQuery<ServiceMonthStatusResponse>({
    queryKey: ['school-orders', 'service-summary', vars],
    queryFn: ({ signal }) => fetchServiceStatusForMonth(vars, signal),
    enabled: Boolean(vars.schoolLocationId),
    staleTime: SERVICE_SUMMARY_STALE_TIME_MS,
  })
}

export function useSchoolServiceDetailsQuery(
  vars: { schoolLocationId: string; date: string; includeStudents?: boolean },
  opts?: { enabled?: boolean },
) {
  return useQuery<ServiceDetailsResponse>({
    queryKey: ['school-orders', 'service-day', vars],
    queryFn: ({ signal }) => fetchServiceDetails(vars, signal),
    enabled: (opts?.enabled ?? true) && Boolean(vars.schoolLocationId) && Boolean(vars.date),
    staleTime: SERVICE_SUMMARY_STALE_TIME_MS,
  })
}

/**
 * Idle (never fetches) while `vars` is null — the CSV export enables it only
 * once the button is pressed. Always stale: an export must read the orders
 * as they are NOW, not a copy from the last press.
 */
export function useStudentOrdersQuery(vars: { schoolLocationId: string; from: string; to: string } | null) {
  return useQuery<StudentOrdersResponse>({
    queryKey: ['school-orders', 'student-orders', vars],
    queryFn: ({ signal }) => fetchStudentOrders(vars!, signal),
    enabled: vars !== null,
    staleTime: 0,
  })
}

export function useCreateCartMutation() {
  return useMutation({ mutationFn: createCart })
}

export function useAddCartItemMutation() {
  return useMutation({ mutationFn: addCartItem })
}

export function useSubmitCartMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: submitCart,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['school-orders', 'forecast-batches'] })
    },
  })
}

// ---------------------------------------------------------------------------
// Multi-group checkout — submit the persistent cart
// ---------------------------------------------------------------------------

/** ISO instant the cart endpoints expect for a `YYYY-MM-DD` service day. */
export function serviceDateIso(dateKey: string): string {
  return `${dateKey}T12:00:00.000Z`
}

export interface CheckoutGroupResult {
  /** `${date}::${mealtime}` — matches the cart store's group key. */
  groupKey: string
  date: string
  mealtime: Mealtime
  ok: boolean
  batch?: SubmitCartResponse
  error?: string
  /** Set when this group failed 409 `price_changed` — drives the consent dialog. */
  priceChanges?: PriceChangeWire[]
  /** Set when this group failed 422 `price_missing` — lines with no authored price. */
  unpricedMenuItemIds?: string[]
  /** Set when this group failed 400 `menu_item_not_available_on_date` — stale lines the menu no longer serves. */
  unavailableMenuItemIds?: string[]
  /** Set when this group failed 402 `insufficient_funds` — drives the "Add funds" link. */
  insufficientFunds?: boolean
  /**
   * Set when this group failed 409 `production_records_overdue` — manual
   * ordering is paused until past service days are completed. Drives the
   * "Ordering paused" banner linking to the Production Records page.
   */
  productionRecordsOverdue?: { incompleteDays: string[] }
}

export interface CheckoutSchoolCartVariables {
  school: AdminSchool
  groups: CheckoutGroup[]
  /** Resubmit consent after the PriceChangedDialog — see `SubmitCartVariables`. */
  acceptRepricing?: boolean
}

export interface CheckoutSchoolCartResult {
  results: CheckoutGroupResult[]
  allOk: boolean
}

/**
 * Run the `createCart → addCartItem(×N) → submitCart` pipeline once per
 * `(serviceDate, mealtime)` group — the unit `submitCart` makes one
 * OrderBatch out of. Groups are submitted sequentially and independently:
 * a failing group is captured (not thrown) so the caller can keep the
 * succeeded groups out of the cart while leaving the failed ones for a
 * retry. The orchestration is deliberately thin; grouping lives in the
 * pure `buildCheckoutPlan` helper so it's unit-tested without this hook.
 */
export async function checkoutSchoolCart(vars: CheckoutSchoolCartVariables): Promise<CheckoutSchoolCartResult> {
  const { school, groups, acceptRepricing } = vars
  const accountId = resolveBillingAccountId(school)
  const results: CheckoutGroupResult[] = []

  for (const group of groups) {
    const isoDate = serviceDateIso(group.date)
    try {
      const cart = await createCart({ accountId, serviceLocationId: school.id })
      if (!cart.id) {
        throw new Error('createCart returned no cart id')
      }
      for (const line of group.lines) {
        // Whole-school (NONE campus) lines are bucket-less: no group
        // tuple on the wire (the server 400s any tuple), just the
        // optional portion tier. Every other line sends the
        // (groupDimension, groupValue) column identity: group lines
        // carry their own tuple; age-group lines send the AGE_GROUP
        // tuple keyed by the column id (the server resolves the portion
        // tier from it).
        const tuple =
          line.columnId === WHOLE_SCHOOL_COLUMN_ID
            ? { gradeRangeId: line.gradeRangeId ?? undefined }
            : line.groupDimension && line.groupValue
              ? { groupDimension: line.groupDimension, groupValue: line.groupValue }
              : { groupDimension: 'AGE_GROUP' as const, groupValue: line.columnId }
        await addCartItem({
          cartId: cart.id,
          menuItemId: line.menuItemId,
          date: isoDate,
          quantity: line.quantity,
          ...tuple,
          selectionFingerprint: '',
          selections: [],
          // A still-derived system-milk line's count is a pure function of
          // the meal counts and the campus's BEVERAGE rate, so mark it
          // DERIVED: a pre-deadline edit may recompute it, while anything a
          // human touched stays put. `isDerivedQuantity` is the ONE definition
          // of that, shared with `committedCartLine` — which is what keeps the
          // flag alive across the add-to-cart commit.
          ...(isDerivedQuantity(line) ? { quantitySource: 'DERIVED' as const } : {}),
          // Component quantities — the numbers the school typed (or the
          // ones derived from the campus's rates and shown to them) pass
          // through UNCHANGED, so displayed is shipped by construction.
          // No opinion at all = ship 100% (the server also no-ops unless
          // the school opted into the default-split prefill). Per-milk-SKU
          // counts ride along as a breakdown of the BEVERAGE total.
          ...(() => {
            const wire = overridesFromQuantities({
              mealQty: line.quantity,
              typed: line.componentQuantities,
              rateBps: line.componentRateBps,
              partCounts: line.componentPartCounts,
              milkSkuQuantities: line.milkSkuQuantities,
            })
            if (!wire) {
              return {}
            }
            return {
              componentQuantityOverrides: wire.componentQuantityOverrides,
              ...(wire.componentSkuQuantityOverrides
                ? { componentSkuQuantityOverrides: wire.componentSkuQuantityOverrides }
                : {}),
              // PROVENANCE — which kinds are the school's OWN numbers. The
              // map above is complete by necessity (a kind missing from a
              // non-null kindCounts ships as 0), so this list is the only
              // thing that lets a later pre-deadline edit re-derive the
              // rest against the campus's current rates. Omitted entirely
              // for a line with no component opinion.
              ...(line.componentTypedKinds ? { componentQuantitiesTypedKinds: [...line.componentTypedKinds] } : {}),
            }
          })(),
        })
      }
      const batch = await submitCart({
        cartId: cart.id,
        organizationId: school.organizationId,
        serviceLocationId: school.id,
        serviceDate: isoDate,
        mealtime: group.mealtime,
        originator: 'SCHOOL_FORECAST',
        ...(acceptRepricing === true ? { acceptRepricing: true } : {}),
      })
      results.push({
        groupKey: groupKey(group.date, group.mealtime),
        date: group.date,
        mealtime: group.mealtime,
        ok: true,
        batch,
      })
    } catch (e) {
      // Typed failures — 409 price_changed / 422 price_missing — keep
      // their bodies so the caller can run the consent loop; anything
      // else stays a plain message for the generic retry box.
      const message = e instanceof Error ? e.message : 'Submit failed'
      const failure: GroupSubmitFailure =
        e instanceof ApiError ? classifyGroupSubmitError(e.status, e.code, e.body, message) : { error: message }
      results.push({
        groupKey: groupKey(group.date, group.mealtime),
        date: group.date,
        mealtime: group.mealtime,
        ok: false,
        ...failure,
      })
    }
  }

  return { results, allOk: results.every((r) => r.ok) }
}

export function useCheckoutSchoolCartMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: checkoutSchoolCart,
    onSuccess: () => {
      // At least one group may have submitted — refresh the calendar.
      void qc.invalidateQueries({ queryKey: ['school-orders', 'forecast-batches'] })
    },
  })
}

export function useCancelSchoolBatchMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: cancelSchoolBatch,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['school-orders', 'forecast-batches'] })
    },
  })
}

// ---------------------------------------------------------------------------
// Plan 11 — forecast overlay + pause/resume
// ---------------------------------------------------------------------------

export const forecastOverlayQueryKey = (vars: { schoolLocationId: string; year: number; month: number }) =>
  ['school-orders', 'forecast-overlays', vars] as const

export function useForecastOverlayQuery(vars: {
  schoolLocationId: string
  year: number
  month: number
  enabled?: boolean
}) {
  return useQuery<ForecastOverlayResponse>({
    queryKey: forecastOverlayQueryKey({
      schoolLocationId: vars.schoolLocationId,
      year: vars.year,
      month: vars.month,
    }),
    queryFn: ({ signal }) =>
      fetchForecastOverlay({ schoolLocationId: vars.schoolLocationId, year: vars.year, month: vars.month }, signal),
    enabled: vars.enabled !== false && Boolean(vars.schoolLocationId),
    staleTime: 30_000,
  })
}

export function usePauseSubscriptionForDateMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: pauseSubscriptionForDate,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['school-orders', 'forecast-overlays'] })
    },
  })
}

export function useResumeSubscriptionForDateMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: resumeSubscriptionForDate,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['school-orders', 'forecast-overlays'] })
    },
  })
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * The Account the school admin bills against. Per-campus Account
 * wins if one exists; otherwise the org-wide Account. Throws if
 * neither — that's a provisioning bug, surface it.
 */
export function resolveBillingAccountId(school: AdminSchool): string {
  const campusAccount = school.ownedAccounts.find((a) => a.kind === 'ORGANIZATION' && !a.isArchived)
  if (campusAccount) {
    return campusAccount.id
  }
  const orgAccount = school.organization.ownedAccounts.find((a) => a.kind === 'ORGANIZATION' && !a.isArchived)
  if (orgAccount) {
    return orgAccount.id
  }
  throw new Error(
    `No ORGANIZATION Account found for school ${school.name} or its parent org. Provisioning likely incomplete.`,
  )
}
